import React from 'react';
import { cn } from '../../../lib/utils';

type Variant = 'plain' | 'bordered' | 'warm';
type Padding = 'none' | 'sm' | 'md' | 'lg';

export interface TaskyCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  padding?: Padding;
  hoverable?: boolean;
  asChild?: boolean;
}

const variantClass: Record<Variant, string> = {
  plain: 'bg-white',
  bordered: 'bg-white border border-warm-border',
  warm: 'bg-warm-card border border-warm-border',
};

const paddingClass: Record<Padding, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

export const TaskyCard = React.forwardRef<HTMLDivElement, TaskyCardProps>(
  (
    { className, variant = 'plain', padding = 'md', hoverable, children, ...rest },
    ref,
  ) => (
    <div
      ref={ref}
      {...rest}
      className={cn(
        'rounded-tasky-md transition-shadow',
        variantClass[variant],
        paddingClass[padding],
        hoverable && 'hover:shadow-card cursor-pointer',
        className,
      )}
    >
      {children}
    </div>
  ),
);
TaskyCard.displayName = 'TaskyCard';

export const TaskyCardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div {...rest} className={cn('mb-3 space-y-1', className)}>
    {children}
  </div>
);

export const TaskyCardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  className,
  children,
  ...rest
}) => (
  <h4 {...rest} className={cn('text-lg font-semibold text-dark', className)}>
    {children}
  </h4>
);

export const TaskyCardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  children,
  ...rest
}) => (
  <p {...rest} className={cn('text-sm text-gray-500', className)}>
    {children}
  </p>
);

export const TaskyCardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div {...rest} className={cn('mt-4 flex items-center gap-2', className)}>
    {children}
  </div>
);
